import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useGlobalAdminStatus } from '@/hooks/useGlobalAdminStatus';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type InvoiceRow = {
  id: string; invoice_number: string; customer_name: string; billing_period: string;
  due_date: string; currency: string; subtotal: string; tax: string; tax_rate_percent: string; tax_treatment: string;
  total: string; status: string; paid_at: string | null; advisor_share: string;
};

const VAT_TREATMENT: Record<string, string> = {
  domestic: 'Czech VAT',
  reverse_charge: 'Reverse charge',
  eu_consumer: 'VAT, no valid VAT ID',
  outside_eu: 'Outside EU',
  not_registered: 'Not a VAT payer',
};

function vatLabel(row: InvoiceRow) {
  if (!row.tax_treatment) return '—';
  const rate = Number(row.tax_rate_percent);
  const name = VAT_TREATMENT[row.tax_treatment] ?? row.tax_treatment;
  return rate > 0 ? `${row.tax} ${row.currency} (${rate}%)` : name;
}

const STATUS_TONE: Record<string, string> = {
  pending: 'bg-amber-500/15 text-amber-700',
  overdue: 'bg-red-500/15 text-red-700',
  paid: 'bg-emerald-500/15 text-emerald-700',
  cancelled: 'bg-muted text-muted-foreground',
  requested: 'bg-amber-500/15 text-amber-700',
  rejected: 'bg-red-500/15 text-red-700',
};

export default function CustomerInvoices() {
  const queryClient = useQueryClient();
  const { role } = useGlobalAdminStatus();
  const canAct = role === 'admin';

  const invoices = useQuery({
    queryKey: ['global-admin-invoices'],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      return (await api.get('/global-admin/invoices/')).data as InvoiceRow[];
    },
  });

  const markPaid = useMutation({
    mutationFn: async (id: string) => {
      const api = await authenticatedBitswanBackendInstance();
      return api.post(`/global-admin/invoices/${id}/mark-paid/`);
    },
    onSuccess: () => { toast.success('Marked paid'); queryClient.invalidateQueries({ queryKey: ['global-admin-invoices'] }); },
    onError: (err: any) => toast.error(err.response?.data?.error || err.message),
  });

  return (
  <Card data-testid="admin-invoices">
    <CardHeader>
      <CardTitle>Customer invoices</CardTitle>
      <CardDescription>Mark an invoice paid when the money arrives. Advisors see their share become available the same moment.</CardDescription>
    </CardHeader>
    <CardContent>
      {invoices.isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr><th className="py-1">Invoice</th><th className="py-1">Customer</th><th className="py-1">Period</th><th className="py-1 text-right">Net</th><th className="py-1 text-right">VAT</th><th className="py-1 text-right">Total</th><th className="py-1 text-right">Advisor share</th><th className="py-1 text-right">Status</th><th /></tr>
          </thead>
          <tbody>
            {(invoices.data ?? []).map((row) => (
              <tr key={row.id} className="border-t" data-testid={`admin-invoice-${row.invoice_number}`}>
                <td className="py-2 font-medium">{row.invoice_number}</td>
                <td className="py-2">{row.customer_name}</td>
                <td className="py-2">{row.billing_period}</td>
                <td className="py-2 text-right text-muted-foreground">{row.subtotal} {row.currency}</td>
                <td className="py-2 text-right text-muted-foreground" title={VAT_TREATMENT[row.tax_treatment] ?? ''} data-testid={`admin-invoice-vat-${row.invoice_number}`}>{vatLabel(row)}</td>
                <td className="py-2 text-right">{row.total} {row.currency}</td>
                <td className="py-2 text-right text-muted-foreground">{row.advisor_share !== '0' ? `${row.advisor_share} ${row.currency}` : '—'}</td>
                <td className="py-2 text-right"><Badge className={STATUS_TONE[row.status] ?? ''}>{row.status}</Badge></td>
                <td className="py-2 text-right">
                  {canAct && (row.status === 'pending' || row.status === 'overdue') && (
                    <Button size="sm" variant="outline" onClick={() => markPaid.mutate(row.id)} disabled={markPaid.isPending} data-testid={`mark-paid-${row.invoice_number}`}>
                      <Check className="mr-1 h-3.5 w-3.5" /> Paid
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {invoices.data?.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No invoices issued yet.</td></tr>}
          </tbody>
        </table>
      )}
    </CardContent>
  </Card>

  );
}
